// client/src/components/pages/RouteSummary/transactionCsv.ts (converted from .js, ST-00216)
//
// CSV for the transaction-details window (per-day lists and the Brought /
// Carried Forward lists). ST-00215 (RSO-5): First / Second Weight are TEXT
// columns, so the API returns them as strings ("49.70"); the old builder called
// `.toFixed` on them, threw a TypeError and saved nothing.
//
// ST-00218 (lm-status): with load matching on, a "Load status" text column (the
// ledger's label, e.g. "Needs review — Probable duplicate weighing") follows
// Ticket No. The Brought / Carried Forward export keeps its older delivery-no
// receipt column too: the export carries every column, shown or not.
//
// ST-00218 P3: a list of a "Truck + time" route (`engine`) has no delivery-no receipt
// column: it carries "Why Carried Forward" (CF) and "Paired Receipt" instead.
import type {
  BfcfVariant,
  TransactionDetailRow,
  TransactionType,
} from '../../../types/clientPages/routeSummary';
import type { TicketLoadStatus } from '../../../types/loadMatching';
import type { CarriedForwardReason } from '../../../types/routeFigures';
import { loadStatusText } from '../../../services/loadStatus';

/** Why a Truck + time dispatch is Carried Forward, in plain words. */
export const CF_REASON_LABELS: Record<CarriedForwardReason, string> = {
  received_after: 'Received after the period',
  in_transit: 'In transit',
  received_before: 'Received before the period',
  pending: 'Not in the ledger yet',
};

/** The reason text of one row (with the receipt date when it was received after the period). */
export const cfReasonText = (
  row: Pick<TransactionDetailRow, 'cfReason' | 'lateReceiptTimestamp'>,
  formatDate: (timestamp: string) => string
): string => {
  if (!row.cfReason) return '';
  const label = CF_REASON_LABELS[row.cfReason] || row.cfReason;
  return row.cfReason === 'received_after' && row.lateReceiptTimestamp
    ? `${label} (${formatDate(row.lateReceiptTimestamp)})`
    : label;
};

// A weight that may arrive as a number or as numeric text. Blank when unknown;
// non-numeric text is passed through as-is rather than dropped.
export const formatNumberCell = (value: unknown, fallback = ''): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  return Number.isFinite(n) ? n.toFixed(2) : String(value);
};

// Every cell is quoted; embedded quotes are doubled so a value with a quote or
// a comma cannot shift the columns.
const csvCell = (value: unknown): string =>
  `"${String(value === null || value === undefined ? '' : value).replace(/"/g, '""')}"`;

/** Options of buildTransactionsCsv. */
export interface TransactionsCsvOptions {
  /** Per-day mode: which side the list is (receipts add a Variance % column). */
  type?: TransactionType | null;
  /** Brought / Carried Forward mode, or null for a per-day list. */
  bfcfVariant?: BfcfVariant | null;
  /** (timestamp) => display date */
  formatDate: (timestamp: string) => string;
  /** (timestamp) => display time */
  formatTime: (timestamp: string) => string;
  /**
   * The Load status labels by ticket uid when load matching is on (adds the "Load status"
   * column; a ticket without a label exports blank). null / omitted: load matching is off,
   * no column.
   */
  loadStatuses?: Record<string, TicketLoadStatus> | null;
  /**
   * ST-00218 P3: the list comes from the load-match ledger (a Truck + time route): no
   * delivery-no receipt column; "Why Carried Forward" (CF) and "Paired Receipt" instead.
   */
  engine?: boolean;
}

/**
 * @param transactions - rows from /details or /bfcf-details
 * @returns CSV text
 */
export const buildTransactionsCsv = (
  transactions: TransactionDetailRow[] | null | undefined,
  {
    type,
    bfcfVariant = null,
    formatDate,
    formatTime,
    loadStatuses = null,
    engine = false,
  }: TransactionsCsvOptions
): string => {
  const isCarriedForward = bfcfVariant === 'carried-forward';
  const legacyReceiptColumn = !!bfcfVariant && !engine;
  const engineColumns = !!bfcfVariant && engine;
  const headers = [
    'Date',
    'Ticket Timestamp',
    'Ticket Type',
    'Ticket No',
    ...(loadStatuses ? ['Load status'] : []),
    'Reg No',
    'Dispatch Location',
    'Receipt Location',
    'Nett Weight (tonnes)',
    'Product',
    'First Weight',
    'Second Weight',
    'Dispatch Organisation',
    'Haulier',
    'Driver',
    'Delivery No',
    ...(legacyReceiptColumn ? [isCarriedForward ? 'Receipt Found After Period' : 'Receipted'] : []),
    ...(engineColumns && isCarriedForward ? ['Why Carried Forward'] : []),
    ...(engineColumns ? ['Paired Receipt'] : []),
    ...(type === 'receipt' ? ['Variance %'] : []),
  ];

  const lines = (transactions || []).map((row) =>
    [
      formatDate(row.ticketTimestamp),
      formatTime(row.ticketTimestamp),
      row.ticketType,
      row.ticketNo,
      ...(loadStatuses ? [loadStatusText(row.ticketUID ? loadStatuses[row.ticketUID] : null)] : []),
      row.vehicleReg || '',
      row.dispatchLocation,
      row.receiptLocation,
      formatNumberCell(row.nettWeight, '0.00'),
      row.product || '',
      formatNumberCell(row.grossWeight),
      formatNumberCell(row.tareWeight),
      row.customer || '',
      row.transporter || '',
      row.driverName || '',
      row.deliveryNo || '',
      ...(legacyReceiptColumn
        ? [
            isCarriedForward
              ? row.lateReceiptTimestamp
                ? formatDate(row.lateReceiptTimestamp)
                : 'No receipt found'
              : row.matchedReceiptTimestamp
                ? formatDate(row.matchedReceiptTimestamp)
                : '',
          ]
        : []),
      ...(engineColumns && isCarriedForward ? [cfReasonText(row, formatDate)] : []),
      ...(engineColumns ? [row.partnerTicketNo || ''] : []),
      ...(type === 'receipt' ? [formatNumberCell(row.variancePercent)] : []),
    ]
      .map(csvCell)
      .join(',')
  );

  return [headers.join(','), ...lines].join('\n');
};
